import * as Sentry from '@sentry/angular';
import { enableProdMode, LOCALE_ID, importProvidersFrom, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AppComponent } from './app/app.component';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { TROVATA_APP_STATES } from './app/core/models/state.model';
import { AuthenticationModule } from './app/core/modules/authentication.module';
import { appRoutes } from './app/app-routing';
import { withInterceptorsFromDi, provideHttpClient } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { provideLottieOptions } from 'ngx-lottie';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { logoutPlugin } from './app/core/store/plugins/logout.plugin';
import { provideStore, withNgxsPlugin } from '@ngxs/store';
import { ENVIRONMENT_TOKEN } from './app/core/models/config.model';
import { HEAP_INITIALIZER_PROVIDER } from './app/core/services/heap-analytics.service';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MY_LUXON_FORMATS } from './app/shared/utils/datepicker-formats';
import { provideRouter, Router } from '@angular/router';
import { provideHighcharts } from 'highcharts-angular';
import { AuthState } from './app/core/store/state/auth/auth.state';
import { PreferencesState } from './app/shared/store/state/preferences/preferences.state';
import { IdentityState } from './app/features/settings/store/state/identity.state';
import { TQLFieldsState } from './app/features/tql/store/state/tql-fields/tql-fields.state';
import { FilterObjectState } from './app/shared/store/state/filter-object/filter-object.state';
import { CurrencyState } from './app/shared/store/state/currency/currency.state';
import { SENTRY_DSN } from '../sentry.config';
import { ChunkErrorHandler } from './app/core/services/chunk-error-handler.service';
import { normalizeLocale } from './app/shared/utils/locale-utils';

Sentry.init({
	dsn: SENTRY_DSN,
	integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
	environment: environment.environmentName,
	enableLogs: true,
	sendDefaultPii: true,
	ignoreErrors: [
		// Ignore specific errors
		'ExpressionChangedAfterItHasBeenCheckedError',
		// Ignore chunk loading errors (these occur when users have cached old app versions)
		'ChunkLoadError',
		'Loading chunk',
		/error loading dynamically imported module/i,
		/Failed to fetch dynamically imported module/i,
		/Loading CSS chunk/i,
	],
	beforeSend(event, hint) {
		// Additional filtering for chunk loading errors that might slip through ignoreErrors
		const error = hint.originalException;
		if (error && typeof error === 'object') {
			const errorMessage = (error as Error).message || String(error);
			// Check if it's a chunk loading error
			if (
				errorMessage.includes('dynamically imported module') ||
				errorMessage.includes('ChunkLoadError') ||
				errorMessage.includes('Loading chunk') ||
				errorMessage.includes('Failed to fetch')
			) {
				return null; // Don't send this event to Sentry
			}
		}
		return event;
	},
});

if (environment.production) {
	enableProdMode();
}

bootstrapApplication(AppComponent, {
	providers: [
		provideRouter(appRoutes),

		// Http + animations
		provideHttpClient(withInterceptorsFromDi()),
		provideAnimations(),

		// NGXS store + plugins (devtools LAST)
		provideStore(
			TROVATA_APP_STATES,
			{ selectorOptions: { suppressErrors: true } },
			withNgxsStoragePlugin({
				keys: [
					AuthState,
					CurrencyState,
					'customerFeature.userAccessConfig',
					'customerFeature.featurePermissions',
					FilterObjectState,
					IdentityState,
					PreferencesState,
					TQLFieldsState,
					'transactions.highVolumeTransactionMode',
					'core.connectionCount',
				],
			}),
			withNgxsPlugin(logoutPlugin),
			withNgxsReduxDevtoolsPlugin({ disabled: environment.production })
		),

		// App/env tokens
		{ provide: ENVIRONMENT_TOKEN, useValue: environment },
		{ provide: LOCALE_ID, useValue: normalizeLocale(navigator.language) },

		// --- Sentry (Angular) ---
		{
			provide: ErrorHandler,
			useFactory: () => new ChunkErrorHandler(Sentry.createErrorHandler()),
		},
		{
			provide: Sentry.TraceService,
			deps: [Router],
		},
		{
			provide: APP_INITIALIZER,
			// ensures TraceService is instantiated early (for routing instrumentation)
			useFactory: () => () => {},
			deps: [Sentry.TraceService],
			multi: true,
		},
		// --- end Sentry ---

		// Heap analytics (loads per-environment app ID, starts autocapture)
		HEAP_INITIALIZER_PROVIDER,

		// 3rd-party providers
		provideLottieOptions({ player: () => import('lottie-web') }),
		provideEnvironmentNgxMask(),
		provideHighcharts(),

		// Only import NgModules that actually contribute providers
		importProvidersFrom(AuthenticationModule, MatSnackBarModule, MatNativeDateModule),

		// Material date adapters
		provideLuxonDateAdapter(),
		{ provide: MAT_DATE_FORMATS, useValue: MY_LUXON_FORMATS },
		{ provide: MAT_DATE_LOCALE, useValue: 'en-US' },
	],
}).catch(err => console.error(err));
